import { useEffect, useState } from "react";

import { Card } from "../Card";
import { StageBadge } from "./ConsentAttestationField";
import type { StageTone } from "./ConsentAttestationField";
import { VideoConfigCard } from "./VideoConfigCard";
import { VideoConsentCard } from "./VideoConsentCard";
import { VideoRenderCard } from "./VideoRenderCard";
import { VideoDispatchModal } from "../../pages/Engagements/VideoDispatchModal";
import type { EngagementRecord } from "../../api/engagements";
import type { DispatchStage } from "../../lib/videoDrill";
import { mapVideoError, sha256Hex, videoDrillStage } from "../../lib/videoDrill";
import { ApiError } from "../../lib/api";
import { useLeaveDispatch, useVideoDispatchStatus } from "../../hooks/useVideo";

// ─── Button styles (local, consistent with the cards) ─────────────────────────

const btnBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: "var(--radius-input)",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 14px",
  minHeight: 36,
  border: "1px solid transparent",
};

const ctaBtn: React.CSSProperties = {
  ...btnBase,
  background: "var(--cta)",
  color: "#1A1108",
  borderColor: "var(--cta)",
};

const ctaBtnDisabled: React.CSSProperties = {
  ...btnBase,
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

const ghostBtn: React.CSSProperties = {
  ...btnBase,
  background: "transparent",
  color: "var(--text-secondary)",
  borderColor: "var(--border)",
};

const ghostBtnDisabled: React.CSSProperties = {
  ...ghostBtn,
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

// ─── Dispatch stage → badge mapping ───────────────────────────────────────────

interface StageMeta {
  label: string;
  tone: StageTone;
}

// StageTone only has muted | warning | success — no "accent" tone; the active
// state uses "warning" (amber) as the closest in-progress signal.
const DISPATCH_STAGE_META: Record<DispatchStage, StageMeta> = {
  none: { label: "Not dispatched", tone: "muted" },
  active: { label: "Bot in call", tone: "warning" },
  left: { label: "Left", tone: "muted" },
};

// ─── VideoDrillPanel ──────────────────────────────────────────────────────────

interface Props {
  rec: EngagementRecord;
}

export function VideoDrillPanel({ rec }: Props) {
  // is_script_approved isn't serialized, so recompute the live script hash here.
  const [liveSha, setLiveSha] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const s = rec.video_config?.script;
    if (!s) {
      setLiveSha(null);
      return;
    }
    void sha256Hex(s).then((h) => {
      if (alive) setLiveSha(h);
    });
    return () => {
      alive = false;
    };
  }, [rec.video_config?.script]);

  const stage = videoDrillStage(rec, liveSha);

  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [leaveErr, setLeaveErr] = useState<string | null>(null);

  // Hooks must run unconditionally — compute once, use results in conditional JSX.
  const dispatchStatus = useVideoDispatchStatus(rec.engagement_id, {
    live: stage.dispatch === "active",
  });
  const leave = useLeaveDispatch(rec.engagement_id);

  function doLeave() {
    setLeaveErr(null);
    leave.mutate(undefined, {
      onError: (e) =>
        setLeaveErr(e instanceof ApiError ? mapVideoError(e.detail) : "Leave failed"),
    });
  }

  const nextHint =
    stage.script !== "approved"
      ? "Next: approve the script"
      : stage.consent !== "complete"
        ? "Next: complete consent"
        : stage.render !== "ready"
          ? "Next: render the clip"
          : rec.state !== "active"
            ? "Next: activate the engagement"
            : "Ready to dispatch";

  const dispatchMeta = DISPATCH_STAGE_META[stage.dispatch];
  const dispatchDisabled = !(rec.state === "active" && stage.render === "ready");
  const vd = rec.video_dispatch;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Header: eyebrow + next-step hint */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.20em",
            color: "var(--accent)",
            textTransform: "uppercase",
          }}
        >
          Video drill
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.04em",
            color: "var(--text-muted)",
          }}
        >
          {nextHint}
        </span>
      </div>

      {/* 1. Script & avatar */}
      <VideoConfigCard engagementId={rec.engagement_id} rec={rec} stage={stage} />

      {/* 2. Consent */}
      <VideoConsentCard engagementId={rec.engagement_id} rec={rec} stage={stage} />

      {/* 3. Render */}
      <VideoRenderCard engagementId={rec.engagement_id} rec={rec} stage={stage} />

      {/* 4. Dispatch */}
      <Card title="" accentColor="var(--cta)">
        {/* Custom header row with title + badge */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 12,
            borderBottom: "1px solid var(--border-subtle)",
            marginBottom: -2,
          }}
        >
          <h5
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              color: "var(--text-primary)",
            }}
          >
            Dispatch
          </h5>
          <StageBadge label={dispatchMeta.label} tone={dispatchMeta.tone} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Existing dispatch status */}
          {vd ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: 11,
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.02em",
                color: "var(--text-secondary)",
              }}
            >
              <span>
                Status:{" "}
                <span style={{ color: "var(--text-primary)" }}>{vd.status}</span>
              </span>
              <span>Dispatched {new Date(vd.dispatched_at).toLocaleString()}</span>
              {stage.dispatch === "active" && dispatchStatus.data ? (
                <span style={{ color: "var(--text-muted)" }}>
                  live: {JSON.stringify(dispatchStatus.data).slice(0, 120)}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Leave error */}
          {leaveErr ? (
            <div style={{ color: "var(--error)", fontSize: 12, fontFamily: "var(--font-sans)" }}>
              {leaveErr}
            </div>
          ) : null}

          {/* Footer actions */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              paddingTop: 12,
              borderTop: "1px solid var(--border-subtle)",
              justifyContent: "flex-end",
            }}
          >
            {dispatchDisabled ? (
              <span
                style={{
                  fontSize: 10.5,
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                  letterSpacing: "0.02em",
                  textAlign: "right",
                }}
              >
                Activate the engagement and render a current clip first.
              </span>
            ) : null}

            {stage.dispatch === "active" ? (
              <button
                onClick={doLeave}
                disabled={leave.isPending}
                style={leave.isPending ? ghostBtnDisabled : ghostBtn}
              >
                {leave.isPending ? "LEAVING…" : "LEAVE CALL"}
              </button>
            ) : null}

            <button
              onClick={() => setDispatchOpen(true)}
              disabled={dispatchDisabled}
              style={dispatchDisabled ? ctaBtnDisabled : ctaBtn}
            >
              DISPATCH DRILL
            </button>
          </div>
        </div>
      </Card>

      {/* 5. Dispatch modal */}
      <VideoDispatchModal
        open={dispatchOpen}
        engagementId={rec.engagement_id}
        engagementName={rec.display_name}
        onClose={() => setDispatchOpen(false)}
      />
    </div>
  );
}
